import { useEffect, useRef, useState } from "react";
import { Popup } from "@/components/common/Popup";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dropdown } from "@/components/ui/filterSelect";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  ChevronRight,
  ChevronDown,
  Folder,
  FolderOpen,
  FileText,
  Plus,
  Trash2,
  Upload,
  X,
  Check,
} from "lucide-react";
import { DOCUMENT_TYPES, DocumentNode } from "./DocumentSchema";
import { set } from "zod";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  apiFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;
  eventId: string;
  onError?: (message: string) => void;
};

const DOCUMENT_CENTER_ACCEPTED_FILE_TYPES = [".pdf", ".zip", ".mp4", ".avi", ".mov", ".wmv"] as const;
const DOCUMENT_CENTER_UPLOAD_ACCEPT = DOCUMENT_CENTER_ACCEPTED_FILE_TYPES.join(",");
const DOCUMENT_CENTER_UPLOAD_CHUNK_SIZE = 5 * 1024 * 1024;

// ─── helpers ─────────────────────────────────────────────────────────────────


/** Mutably toggle _expanded on the node matching targetId. */
function toggleExpanded(nodes: DocumentNode[], targetId: number | undefined, tempKey: string): DocumentNode[] {
  return nodes.map((n) => {
    const key = n.Id != null ? String(n.Id) : tempKey;
    if (key === tempKey) {
      return { ...n, _expanded: !n._expanded };
    }
    return { ...n, Children: n.Children ? toggleExpanded(n.Children, undefined, tempKey) : n.Children };
  });
}

function upsertNode(nodes: DocumentNode[], parentId: number | null, newNode: DocumentNode, nodeId: (n: DocumentNode) => number): DocumentNode[] {
  if (parentId === null || parentId === 0) {
    return [...nodes, newNode];
  }
  return nodes.map((n) => {
    if (nodeId(n) === parentId) {
      return { ...n, _expanded: true, Children: [...(n.Children ?? []), newNode] };
    }
    return { ...n, Children: n.Children ? upsertNode(n.Children, parentId, newNode, nodeId) : n.Children };
  });
}

function replaceNode(nodes: DocumentNode[], nodeId: number, nextNode: DocumentNode): DocumentNode[] {
  return nodes.map((node) => {
    if (node.Id === nodeId) {
      return {
        ...node,
        ...nextNode,
        Children: nextNode.Children ?? node.Children,
      };
    }

    return {
      ...node,
      Children: node.Children ? replaceNode(node.Children, nodeId, nextNode) : node.Children,
    };
  });
}




function assignTempKeys(nodes: DocumentNode[]): (DocumentNode )[] {


  const nodesById = new Map<number, DocumentNode>();
  for (const node of nodes) {
    if (node.Id != null) {
      nodesById.set(node.Id, node);
    }
  }

  const roots: DocumentNode[] = [];

  for (const node of nodes) {
    const parentId = node.Parent_Id ?? node.Parent_Id;
    if (parentId == null) {
      roots.push(node);
      continue;
    }

    const parent = nodesById.get(parentId);
    if (!parent) {
      roots.push(node);
      continue;
    }

    parent.Children = [...(parent.Children ?? []), node];
  }

  const normalizeChildren = (treeNodes: DocumentNode[]): DocumentNode[] =>
    treeNodes.map((node) => ({
      ...node,
      Children: node.Children && node.Children.length > 0 ? normalizeChildren(node.Children) : undefined,
    }));

  return normalizeChildren(roots);
}

function getFileNameFromUploadResponse(responseValue: unknown, fallbackFileName: string): string {
  if (typeof responseValue === "string") {
    const trimmed = responseValue.trim();
    if (!trimmed) {
      return fallbackFileName;
    }

    const pathParts = trimmed.split(/[/\\]/);
    return pathParts[pathParts.length - 1] || fallbackFileName;
  }

  if (responseValue && typeof responseValue === "object") {
    const data = responseValue as Record<string, unknown>;
    const candidate =
      data.filename ??
      data.fileName ??
      data.Filename ??
      data.FileName ??
      data.key ??
      data.Key ??
      data.path ??
      data.Path ??
      fallbackFileName;

    return getFileNameFromUploadResponse(candidate, fallbackFileName);
  }

  return fallbackFileName;
}

// ─── sub-components ───────────────────────────────────────────────────────────



type EditState = { id: number; node: DocumentNode };
type DeleteConfirmState = { node: DocumentNode; hasSubitems: boolean };

type TreeRowProps = {
  node: DocumentNode ;
  depth: number;
  onToggle: (id: number) => void;
  onStartAdd: (node: DocumentNode) => void;
  onStartEdit: (id: number, node: DocumentNode) => void;
  onDelete: (node: DocumentNode) => void;
  editId: number | null;
  onEndEdit: (node: DocumentNode, text:string) => void;
};

function TreeRow({ node, depth, onToggle, onStartAdd, onStartEdit, onDelete, editId, onEndEdit}: TreeRowProps) {
  const isEditing = editId === node.Id;
  const isFolder = node.HierarchyLevel != 2;
  const hasChildren = (node.Children?.length ?? 0) > 0;
  const indent = depth * 20;

  return (
    <>
      <tr
      onClick={() => onStartEdit(node.Id, node) }
        className={`border-t align-middle cursor-pointer group ${isEditing ? "bg-blue-50" : "hover:bg-slate-50"}`}
      >
        <td className="py-1 pr-2" style={{ paddingLeft: `${indent + 8}px` }}>
          <div className="flex items-center gap-1">
            {/* expand/collapse chevron for folders */}
            {isFolder && hasChildren ? (
              <button
                type="button"
                className="p-0.5 text-slate-500 hover:text-slate-700"
                onClick={(e) => {
                  e.stopPropagation();
                  onToggle(node.Id);
                }}
              >
                {node._expanded
                  ? <ChevronDown className="h-3.5 w-3.5" />
                  : <ChevronRight className="h-3.5 w-3.5" />}
              </button>
            ) : (
              <span className="w-5 inline-block" />
            )}

            {/* icon */}
            {isFolder
              ? (node._expanded
                  ? <FolderOpen className="h-4 w-4 text-amber-500 shrink-0" />
                  : <Folder className="h-4 w-4 text-amber-500 shrink-0" />)
              : <FileText className="h-4 w-4 text-blue-500 shrink-0" />}

            {/* name */}
            {isEditing && isFolder ? (
              <Input
                autoFocus
                defaultValue={node.Name}
                
                onBlur={(e) => onEndEdit(node, e.currentTarget.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    onEndEdit(node, e.currentTarget.value);
                  } 
                }}
                className="ml-1 h-7"
              />

            )
            :
            (
            <span className="ml-1 text-sm">{node.Name || <span className="text-gray-400 italic">Unnamed</span>}</span>
            )
          }
          </div>
        </td>

        {/* document-only extra info */}
        <td className="py-1 px-2 text-xs text-gray-500">{!isFolder ? node.DocumentType ?? "" : ""}</td>
        <td className="py-1 px-2 text-xs text-gray-500 max-w-[200px] truncate">{!isFolder ? node.URL ?? "" : ""}</td>

        {/* actions */}
        <td className="py-1 px-2">
          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            {isFolder && (
              <button
                type="button"
                title="Add child"
                className="p-1 rounded hover:bg-slate-200 text-slate-500 hover:text-slate-700"
                onClick={(e) => {
                  e.stopPropagation();
                  onStartAdd(node);
                }}
              >
                <Plus className="h-3.5 w-3.5" />
              </button>
            )}
          
            <button
              type="button"
              title="Delete"
              className="p-1 rounded hover:bg-red-100 text-slate-500 hover:text-red-600"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(node);
              }}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        </td>
      </tr>

      {/* recursively render children when expanded */}
      {isFolder && node._expanded && node.Children?.map((child) => (
        <TreeRow
          key={(child as DocumentNode).Id}
          node={child as DocumentNode }
          depth={depth + 1}
          onToggle={onToggle}
          onStartAdd={onStartAdd}
          onStartEdit={onStartEdit}
          onDelete={onDelete}
          editId={editId}
          onEndEdit={onEndEdit}
        />
      ))}
    </>
  );
}
type Lookup = { key: string; filename: string };

// ─── main popup ──────────────────────────────────────────────────────────────

export function DocumentCenterPopup({ isOpen, onClose, apiFetch, eventId, onError }: Props) {
  const [tree, setTree] = useState<(DocumentNode )[]>([]);
  const [loading, setLoading] = useState(false);
  const [addMenu, setAddMenu] = useState<DocumentNode | null>(null);
  const [editState, setEditState] = useState<EditState | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<DeleteConfirmState | null>(null);
  const [fileList, setFileList] = useState<Lookup[]>([]);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const wasOpenRef = useRef(false);
  const uploadInputRef = useRef<HTMLInputElement | null>(null);


  const findNodeById = (
    nodes: (DocumentNode )[],
    id: number
  ): (DocumentNode ) | null => {
    for (const node of nodes) {
      if (node.Id === id) {
        return node;
      }
      if (node.Children) {
        const match = findNodeById(node.Children as (DocumentNode )[], id);
        if (match) {
          return match;
        }
      }
    }

    return null;
  };

  // ── load ────────────────────────────────────────────────────────────────────
  const loadTree = async () => {
    try {
      setLoading(true);
      const res = await apiFetch(`/api/EventPool/GetDocumentCenter?eventId=${eventId}`);
      if (!res.ok) { setTree([]); return; }
      const data: DocumentNode[] = await res.json();
      setTree(assignTempKeys(data ?? []));
    } catch {
      onError?.("Failed to load document center");
      setTree([]);
    } finally {
      setLoading(false);
    }
  };

   const loadFileList = async () => {
    try {
      const res = await apiFetch(`/api/eventpool/GetDocumentCenterFileList?eventId=${eventId}`);
      if (!res.ok) { setFileList([]); return; }
      const data = await res.json();
      setFileList(data);
    } catch {
      onError?.("Failed to load document center");
      setFileList([]);
    } 
  };

  useEffect(() => {
    const openedNow = isOpen && !wasOpenRef.current;
    if (openedNow) loadTree();
    wasOpenRef.current = isOpen;
  }, [isOpen]);

  useEffect(() => {
   
    if (editState && editState.node.DocumentType != "URL" && fileList.length == 0) loadFileList();
   
  }, [ editState?.node.DocumentType]);

 
  // ── helpers ─────────────────────────────────────────────────────────────────
  const toggleNode = (id: number) => {
    setTree((prev) =>
      prev.map((n) => {
        const toggle = (nodes: (DocumentNode)[]): (DocumentNode)[] =>
          nodes.map((node) => {
            if (node.Id === id) return { ...node, _expanded: !node._expanded };
            return { ...node, Children: node.Children ? toggle(node.Children as (DocumentNode)[]) : node.Children };
          });
        return toggle([n])[0];
      })
    );
  };

  const addNode = (parent: DocumentNode | null, type: "folder" | "document") => {
    const newNode: DocumentNode  = {
      Id: 0,
      Name: type === "folder" ? "New folder" : "New document",
      Parent_Id: parent?parent.Id:null,
      HierarchyLevel: type === "folder" ? (parent ? 1 : 0) : 2,
      _expanded: false,
      Children: type === "folder" ? [] : undefined,
    };
    setAddMenu(null);

    if(type === "folder")//save right away
    {
      saveNode(newNode);
    }
    else
      setEditState({ id: 0, node: newNode });

  };

  const requestDelete = (node: DocumentNode) => {
    setDeleteConfirm({
      node,
      hasSubitems: (node.Children?.length ?? 0) > 0,
    });
  };

  const deleteNode_ = async (id: number) => {

const res = await apiFetch(`/api/EventPool/DeleteDocumentCenterItem?key=${id}`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" }
      });

      if (!res.ok) {
        onError?.("Failed to delete node: " + res.statusText);
        return;
      }


    const remove = (nodes: (DocumentNode)[]): (DocumentNode)[] =>
      nodes
        .filter((n) => n.Id !== id)
        .map((n) => ({ ...n, Children: n.Children ? remove(n.Children as (DocumentNode )[]) : n.Children }));
    setTree((prev) => remove(prev));
    if (editState?.id === id) setEditState(null);
    setDeleteConfirm(null);
  };


const nameChanged = async (node: DocumentNode, newName: string) => {

  console.log(node);
  if(node.Name === newName) return;

  node.Name = newName;
  const res = await apiFetch(`/api/EventPool/PutDocumentCenterItem?key=${node.Id}&values=${JSON.stringify({ Name: newName })}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" }
      });

      if (!res.ok) {
        onError?.("Failed to save node: " + res.statusText);
        return;
      }

  
      setTree((prev) =>
         replaceNode(prev, node.Id, node)
      );
  
};
 const saveEdit = async () => {
    if (!editState) return;
    const { id, node } = editState;
    console.log(node);
    if(node.Id == 0)
      saveNode(node);
    else
      updateNode(node);
 }
  const updateNode = async (node: DocumentNode) => {
   

    try {
      
       const res = await apiFetch(`/api/EventPool/PutDocumentCenterItem?key=${node.Id}&values=${JSON.stringify(node)}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" }
      });

      if (!res.ok) {
        onError?.("Failed to save node: " + res.statusText);
        return;
      }

     
     

      setTree((prev) =>
           replaceNode(prev, node.Id, node)
       
         
      );
    } catch {
      onError?.("Failed to save node");
    } finally {
      setEditState(null);
    }
  };
const saveNode = async (node: DocumentNode) => {
  

    try {
      const res = await apiFetch(`/api/EventPool/PostNewDocumentCenterItem?values=${JSON.stringify({ ...node, EventId: eventId })}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" }
      });

      if (!res.ok) {
        onError?.("Failed to save node: " + res.statusText);
        return;
      }

      const saved = await res.json();
      if (!saved?.Id) {
        return;
      }

      const nodeWithId: DocumentNode = {
        ...node,
        ...saved,
        Id: saved.Id,
        OrderNumber: saved.OrderNumber ?? node.OrderNumber,
      };

      setTree((prev) =>
           upsertNode(prev, node.Parent_Id ?? null, nodeWithId, (n) => n.Id ?? 0)
        
      );
    } catch {
      onError?.("Failed to save node");
    } finally {
      setEditState(null);
    }
  };
  const handleUploadClick = () => {
    uploadInputRef.current?.click();
  };

  const handleUploadFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";

    if (!file || !editState) {
      return;
    }

    const fileExtension = file.name.slice(file.name.lastIndexOf(".")).toLowerCase();
    if (!DOCUMENT_CENTER_ACCEPTED_FILE_TYPES.includes(fileExtension as (typeof DOCUMENT_CENTER_ACCEPTED_FILE_TYPES)[number])) {
      onError?.(`Unsupported file type. Allowed: ${DOCUMENT_CENTER_UPLOAD_ACCEPT}`);
      return;
    }

    setIsUploadingFile(true);

    try {
      const totalCount = Math.max(1, Math.ceil(file.size / DOCUMENT_CENTER_UPLOAD_CHUNK_SIZE));
      const fileGuid = crypto.randomUUID();
      let uploadResponse: unknown = file.name;

      for (let index = 0; index < totalCount; index++) {
        const chunkStart = index * DOCUMENT_CENTER_UPLOAD_CHUNK_SIZE;
        const chunkEnd = Math.min(chunkStart + DOCUMENT_CENTER_UPLOAD_CHUNK_SIZE, file.size);
        const chunk = file.slice(chunkStart, chunkEnd, file.type);
        const formData = new FormData();
        const chunkMetadata = {
          Index: index,
          TotalCount: totalCount,
          FileSize: file.size,
          FileName: file.name,
          FileType: file.type,
          FileGuid: fileGuid,
        };

        formData.append("eventId", eventId);
        formData.append("chunkMetadata", JSON.stringify(chunkMetadata));
        formData.append("myFile", chunk, file.name);

        const res = await apiFetch(`/api/EventPool/UploadDocumentCenterFile`, {
          method: "POST",
          body: formData,
        });

        if (!res.ok) {
          onError?.((await res.text()) || "File upload failed");
          return;
        }

        if (index === totalCount - 1) {
          const contentType = res.headers.get("content-type") ?? "";
          uploadResponse = contentType.includes("application/json")
            ? await res.json()
            : await res.text();
        }
      }

      const uploadedFileName = getFileNameFromUploadResponse(uploadResponse, file.name);

      const uploadedLookup = { key: uploadedFileName, filename: uploadedFileName };
      setFileList((prev) => {
        const withoutMatch = prev.filter((item) => item.key !== uploadedLookup.key);
        return [...withoutMatch, uploadedLookup];
      });
      setEditState((prev) => prev ? { ...prev, node: { ...prev.node, URL: uploadedFileName } } : prev);
    } catch {
      onError?.("File upload failed");
    } finally {
      setIsUploadingFile(false);
    }
  };
  const isEditing = editState !== null;

  return (
    <Popup
      isOpen={isOpen}
      onClose={onClose}
      title="Document Center"
      className="w-[95vw] max-w-5xl"
    >
      <input
        ref={uploadInputRef}
        type="file"
        accept={DOCUMENT_CENTER_UPLOAD_ACCEPT}
        className="hidden"
        onChange={handleUploadFileSelected}
      />
      <div className="flex gap-4 min-h-[400px]">

        {/* ── Left: tree ────────────────────────────────────────────────── */}
        <div className="flex-1 overflow-auto">
          <div className="mb-3">
            <span className="text-sm text-gray-500">{loading ? "Loading…" : `${tree.length === 0 ? "No items yet" : ""}`}</span>
          </div>

          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-left">
              <tr>
                <th className="px-2 py-2 font-medium text-gray-600">
                  <div className="flex items-center gap-2">
                    <span>Name</span>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => addNode(null, "folder")}
                    >
                      <Plus className="h-4 w-4 mr-1" /> Add root folder
                    </Button>
                  </div>
                </th>
                <th className="px-2 py-2 font-medium text-gray-600 w-[80px]">Type</th>
                <th className="px-2 py-2 font-medium text-gray-600 w-[180px]">File / URL</th>
                <th className="w-[100px]"></th>
              </tr>
            </thead>
            <tbody>
              {!loading && tree.length === 0 && (
                <tr>
                  <td colSpan={4} className="text-center text-gray-400 py-8">
                    No documents yet — click "Add root folder" to start
                  </td>
                </tr>
              )}
              {tree.map((node) => (
                <TreeRow
                  node={node}
                  depth={0}
                  onToggle={toggleNode}
                  onStartAdd={(curentNode) => setAddMenu(curentNode)}
                  onStartEdit={(id, n) => setEditState({ id, node: { ...n } })}
                  
                  onDelete={requestDelete}
                  editId={editState?.id ?? null}
                  onEndEdit={(curentNode, text) => {nameChanged(curentNode, text); setEditState(null); }}
                />
              ))}
            </tbody>
          </table>
        </div>

        {/* ── Right: edit panel ─────────────────────────────────────────── */}
        {isEditing && editState.node.HierarchyLevel === 2 && (
          <div className="w-72 border-l pl-4 flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <span className="font-semibold text-sm">
                 Document
              </span>
              <button type="button" onClick={() => setEditState(null)} className="text-gray-400 hover:text-gray-600">
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Name */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Name</label>
              <Input
                value={editState.node.Name}
                onChange={(e) => setEditState((s) => s ? { ...s, node: { ...s.node, Name: e.target.value } } : s)}
              />
            </div>

            {editState.node.HierarchyLevel === 2 && (
              <>
                {/* Description */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
                  <Input
                    value={editState.node.Description ?? ""}
                    onChange={(e) => setEditState((s) => s ? { ...s, node: { ...s.node, Description: e.target.value } } : s)}
                  />
                </div>

                {/* Document type */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Document Type</label>
                  <Dropdown
                    source={DOCUMENT_TYPES as unknown as { label: string; value: string }[]}
                    value={editState.node.DocumentType ?? ""}
                    getLabel={(x) => x.label}
                    getValue={(x) => x.value}
                    onChange={(v) => setEditState((s) => s ? { ...s, node: { ...s.node, DocumentType: String(v) } } : s)}
                  />
                </div>

                {/* File name */}
                {editState.node.DocumentType === "URL" ? (
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">  URL</label>
                  <Input
                    value={editState.node.URL ?? ""}
                    onChange={(e) => setEditState((s) => s ? { ...s, node: { ...s.node, URL: e.target.value } } : s)}
                  />
                </div>):(
<div>
                  <div className="mb-1 flex items-center gap-2">
                    <label className="block text-xs font-medium text-gray-600"> File Name (file must be located in protected\\event_id\\DocumentCenter)</label>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            title="upload file"
                            className="rounded p-1 text-gray-500 hover:bg-slate-100 hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
                            onClick={handleUploadClick}
                            disabled={isUploadingFile}
                          >
                            <Upload className="h-3.5 w-3.5" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent>upload file</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  <Dropdown
                    source={fileList.filter(f => f.key != "DocumentCenter")}
                    value={editState.node.URL ?? ""}
                    getLabel={(x) => x.filename}
                    getValue={(x) => x.key}
                  onChange={(v) => setEditState((s) => s ? { ...s, node: { ...s.node, URL: String(v) } } : s)}

                  />
                </div>

                )}
              </>
            )}

            <div className="flex gap-2 mt-auto pt-2">
               <Button className="w-full bg-[#c62828]" onClick={saveEdit}>Save</Button>
             
            </div>
          </div>
        )}
      </div>

      {/* ── Add menu overlay ─────────────────────────────────────────────── */}
      {addMenu && (
        <div className="fixed inset-0 z-10 flex items-center justify-center" onClick={() => setAddMenu(null)}>
          <div
            className="bg-white border rounded-lg shadow-xl p-4 flex gap-3"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="flex flex-col items-center gap-2 px-6 py-4 rounded-lg hover:bg-amber-50 border hover:border-amber-300 transition-colors"
              onClick={() => addNode(addMenu, "folder")}
            >
              <Folder className="h-8 w-8 text-amber-500" />
              <span className="text-sm font-medium">Folder</span>
            </button>
            <button
              type="button"
              className="flex flex-col items-center gap-2 px-6 py-4 rounded-lg hover:bg-blue-50 border hover:border-blue-300 transition-colors"
              onClick={() => addNode(addMenu, "document")}
            >
              <FileText className="h-8 w-8 text-blue-500" />
              <span className="text-sm font-medium">Document</span>
            </button>
            <button
              type="button"
              className="absolute top-2 right-2 text-gray-400 hover:text-gray-600"
              onClick={() => setAddMenu(null)}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
      <Popup
        isOpen={deleteConfirm !== null}
        onClose={() => setDeleteConfirm(null)}
        title="Delete item"
        className="w-[95vw] max-w-md"
      >
        <div className="flex flex-col gap-4">
          <p className="text-sm text-gray-700">
            {deleteConfirm?.hasSubitems
              ? `Delete "${deleteConfirm.node.Name}" and all subitems?`
              : `Delete "${deleteConfirm?.node.Name}"?`}
          </p>
          <div className="flex justify-end gap-2">
           
            <Button
              type="button"
              className="bg-[#c62828] hover:bg-[#b71c1c]"
              onClick={() => deleteConfirm && deleteNode_(deleteConfirm.node.Id)}
            >
              Delete
            </Button>
             <Button type="button" variant="outline" onClick={() => setDeleteConfirm(null)}>
              Cancel
            </Button>
          </div>
        </div>
      </Popup>
    </Popup>
  );
}
